import { useCallback, useState } from "react";
import { type Address } from "viem";
import { useWalletClient } from "wagmi";

import { CADC, USDC } from "@/config/tokens";

import {
  approveCadc,
  approveUsdc,
  authorizeMorpho,
  setAjnaPositionOperator,
} from "@/lib/loop/execute";
import { buildPublicClient } from "@/lib/wallet/clients";

export type SetupMode = "open" | "close";

type Props = {
  readonly account: Address;
  readonly looper: Address;

  readonly mode: SetupMode;
  /** Whether a token approval step is needed for this mode/source combo. */
  readonly tokenApprovalNeeded: boolean;
  /** Open mode: CADC allowance. Close mode: USDC allowance. */
  readonly tokenApproved: boolean;
  /** Whether the venue requires Morpho-style `setAuthorization`. */
  readonly morphoAuthorizationNeeded: boolean;
  readonly morphoAuthorized: boolean;
  /** Ajna only: whether the user's AjnaPosition has this looper as operator. */
  readonly ajnaOperatorNeeded: boolean;
  readonly ajnaOperatorSet: boolean;
  /** Ajna only: the user's AjnaPosition address (for the setOperator call). */
  readonly ajnaPositionAddress: Address | null;
  readonly onCompleted: () => void;
};

type StepId = "approve" | "authorize" | "ajna-operator";

type Busy = StepId | null;

const runTx = async (
  submit: () => Promise<`0x${string}`>,
): Promise<void> => {
  const hash = await submit();
  await buildPublicClient().waitForTransactionReceipt({ hash });
};

type RowState = "done" | "active" | "locked";

const StepRow = ({
  index,
  title,
  detail,
  state,
  busy,
  onAction,
  actionLabel,
}: {
  readonly index: number;
  readonly title: string;
  readonly detail: string;
  readonly state: RowState;
  readonly busy: boolean;
  readonly onAction: () => void;
  readonly actionLabel: string;
}) => {
  const done = state === "done";
  const badgeBg = done
    ? "var(--accent-jade)"
    : state === "active"
      ? "var(--ink-1)"
      : "transparent";
  const badgeColor = done || state === "active" ? "#fbf6ec" : "var(--ink-4)";

  return (
    <li className="flex items-center gap-4">
      <span
        aria-hidden
        className="flex-none grid place-items-center w-7 h-7 rounded-full font-mono tabular"
        style={{
          background: badgeBg,
          color: badgeColor,
          border: done || state === "active" ? "none" : "1px solid var(--line-2)",
        }}
      >
        {done
          ? <span style={{ fontSize: 16, lineHeight: 1 }}>✓</span>
          : <span style={{ fontSize: 11 }}>{index}</span>
        }
      </span>

      <div className="flex-1 min-w-0">
        <div
          className="text-[13px] tracking-[0.02em]"
          style={{ color: state === "locked" ? "var(--ink-4)" : "var(--ink-1)" }}
        >
          {title}
        </div>
        <div
          className="font-mono text-[10px] tracking-[0.06em] mt-0.5"
          style={{ color: "var(--ink-4)" }}
        >
          {detail}
        </div>
      </div>

      {done ? (
        <span
          className="flex-none font-mono text-[10px] tracking-[0.16em] uppercase"
          style={{ color: "var(--accent-jade)" }}
        >
          Done
        </span>
      ) : (
        <button
          type="button"
          disabled={state !== "active" || busy}
          onClick={onAction}
          className="flex-none h-9 px-4 rounded-[2px] text-[11px] tracking-[0.14em] uppercase transition-all border"
          style={
            state === "active" && !busy
              ? {
                  background: "var(--ink-1)",
                  color: "var(--bg-cool)",
                  borderColor: "var(--ink-1)",
                  fontWeight: 500,
                }
              : {
                  background: "transparent",
                  color: "var(--ink-4)",
                  borderColor: "var(--line-2)",
                  fontWeight: 500,
                }
          }
        >
          {busy ? "Confirm…" : actionLabel}
        </button>
      )}
    </li>
  );
};

/**
 * First-time setup sequence shown in the loop card: the user must grant a token
 * allowance and (for Morpho) authorize the looper on Morpho Blue before the
 * main action becomes available. Steps unlock in order.
 */
export const SetupSteps = ({
  account,
  looper,
  mode,
  tokenApprovalNeeded,
  tokenApproved,
  morphoAuthorizationNeeded,
  morphoAuthorized,
  ajnaOperatorNeeded,
  ajnaOperatorSet,
  ajnaPositionAddress,
  onCompleted,
}: Props) => {
  const walletClient = useWalletClient();
  const isOpen = mode === "open";
  const approveLabel = isOpen ? CADC.symbol : USDC.symbol;
  const approveDetail = isOpen
    ? "Allow the looper to pull your principal"
    : "Allow the looper to pull USDC for debt repayment";
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);

  const act = useCallback(
    async (step: StepId) => {
      if (!walletClient.data) {
        setError("Wallet client missing");
        return;
      }
      setError(null);
      setBusy(step);
      try {
        await runTx(() => {
          if (step === "approve") {
            return isOpen
              ? approveCadc(walletClient.data, { account, looper })
              : approveUsdc(walletClient.data, { account, looper });
          }
          if (step === "ajna-operator") {
            if (!ajnaPositionAddress) throw new Error("Position address not loaded");
            return setAjnaPositionOperator(walletClient.data, {
              account,
              positionAddress: ajnaPositionAddress,
              looper,
            });
          }
          return authorizeMorpho(walletClient.data, { account, looper });
        });
        onCompleted();
      } catch (e) {
        console.error(e);
        const msg = (e as { details?: string; message?: string })?.details ?? (e as { message?: string })?.message ?? "Transaction failed";
        setError(msg);
      } finally {
        setBusy(null);
      }
    },
    [account, looper, ajnaPositionAddress, onCompleted, isOpen, walletClient.data],
  );

  let idx = 0;
  const tokenIdx = tokenApprovalNeeded ? ++idx : 0;
  const authIdx = morphoAuthorizationNeeded ? ++idx : 0;
  const ajnaOpIdx = ajnaOperatorNeeded ? idx + 1 : 0;

  const prevTokenDone = !tokenApprovalNeeded || tokenApproved;

  return (
    <section
      className="mb-5 p-5 rounded-[var(--radius-sm)]"
      style={{ border: "1px solid var(--line-2)", background: "var(--line-1)" }}
    >
      <div
        className="font-mono text-[10px] tracking-[0.18em] uppercase mb-4"
        style={{ color: "var(--ink-4)" }}
      >
        One-time setup
      </div>
      <ul className="space-y-4">
        {tokenApprovalNeeded && (
          <StepRow
            index={tokenIdx}
            title={`Approve ${approveLabel}`}
            detail={approveDetail}
            state={tokenApproved ? "done" : "active"}
            busy={busy === "approve"}
            onAction={() => act("approve")}
            actionLabel="Approve"
          />
        )}
        {morphoAuthorizationNeeded && (
          <StepRow
            index={authIdx}
            title="Authorize Morpho"
            detail="Let the looper manage this position on Morpho"
            state={
              morphoAuthorized
                ? "done"
                : prevTokenDone
                  ? "active"
                  : "locked"
            }
            busy={busy === "authorize"}
            onAction={() => act("authorize")}
            actionLabel="Authorize"
          />
        )}
        {ajnaOperatorNeeded && (
          <StepRow
            index={ajnaOpIdx}
            title="Authorize looper"
            detail="Grant the looper permission to manage your Ajna position"
            state={
              ajnaOperatorSet
                ? "done"
                : prevTokenDone
                  ? "active"
                  : "locked"
            }
            busy={busy === "ajna-operator"}
            onAction={() => act("ajna-operator")}
            actionLabel="Authorize"
          />
        )}
      </ul>
      {error && (
        <p
          className="mt-4 font-mono text-[11px] tracking-[0.04em]"
          style={{ color: "var(--accent-rose)" }}
        >
          {error}
        </p>
      )}
    </section>
  );
};
